/**
 * FattureInCloud - SuperAdmin Only
 * Shows app-generated invoices for subscription purchases
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, RefreshCw, FileText, Building2, ExternalLink, Loader2, Send, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FattureInCloudStatus() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check if user is super admin
  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error('Accesso non autorizzato');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/fattureincloud/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.status === 403) {
        toast.error('Accesso riservato ai SuperAdmin');
        navigate('/dashboard');
        return;
      }
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      } else {
        setStatus({ enabled: false, connection: { connected: false, error: 'Errore di connessione' } });
      }
    } catch (error) {
      setStatus({ enabled: false, connection: { connected: false, error: error.message } });
    }
  }, [token, navigate]);

  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/fattureincloud/app-invoices?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchStatus();
      await fetchInvoices();
      setIsLoading(false);
    };
    
    loadData();
  }, [fetchStatus, fetchInvoices]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchStatus();
    await fetchInvoices();
    setIsRefreshing(false);
    toast.success('Dati aggiornati');
  };

  const handleRetryInvoice = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/fattureincloud/retry-invoice/${invoiceId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Fattura emessa con successo');
        await fetchInvoices();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore emissione fattura');
      }
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'ISSUED': { label: 'Emessa', variant: 'default', className: 'bg-green-500' },
      'PENDING': { label: 'In attesa', variant: 'secondary', className: 'bg-yellow-500' },
      'ERROR': { label: 'Errore', variant: 'destructive', className: '' },
      'FIC_CLIENT_ERROR': { label: 'Errore Cliente', variant: 'destructive', className: '' },
      'FIC_INVOICE_ERROR': { label: 'Errore Fattura', variant: 'destructive', className: '' }
    };
    
    const config = statusMap[status] || { label: status, variant: 'secondary', className: '' };
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  if (!user?.is_super_admin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <PageHeader
        title="FattureInCloud"
        subtitle="Stato integrazione fatturazione"
        actions={
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Aggiorna
          </Button>
        }
      />

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Stato FattureInCloud
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.connection?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-700">Connesso</p>
                  <p className="text-sm text-muted-foreground">
                    Le fatture vengono emesse automaticamente
                  </p>
                </div>
                <Badge variant="default" className="ml-auto bg-green-500">Attivo</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Azienda Emittente</p>
                  <p className="font-medium">{status.connection.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Company ID</p>
                  <p className="font-medium font-mono">{status.connection.company_id}</p>
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Non connesso:</strong> {status?.connection?.error || 'Configurazione mancante'}
                <p className="text-sm mt-2">
                  Le fatture verranno salvate localmente e potranno essere emesse successivamente.
                </p>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* App Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Fatture Abbonamenti
          </CardTitle>
          <CardDescription>
            Fatture elettroniche emesse per acquisti piani e upgrade
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>N. FIC</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Piano</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="text-sm">
                      {formatDate(inv.created_at)}
                    </TableCell>
                    <TableCell className="font-mono">
                      {inv.fic_invoice_number || '-'}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium truncate max-w-[150px]">{inv.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{inv.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{inv.plan_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {inv.billing_period === 'monthly' ? 'Mensile' : 'Annuale'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <p className="font-medium">{formatCurrency(inv.amount_gross)}</p>
                        <p className="text-xs text-muted-foreground">
                          IVA {formatCurrency(inv.amount_vat)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(inv.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {inv.fic_invoice_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(inv.fic_invoice_url, '_blank')}
                            title="Apri su FattureInCloud"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        {inv.status !== 'ISSUED' && status?.connection?.connected && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetryInvoice(inv.id)}
                            title="Riprova emissione"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Nessuna fattura emessa</p>
              <p className="text-sm mt-2">
                Le fatture verranno create automaticamente quando un cliente acquista un abbonamento
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Send className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Emissione Automatica</p>
              <p>
                Quando un cliente acquista un piano (mensile o annuale), viene automaticamente 
                creata e inviata una fattura elettronica a SDI tramite FattureInCloud.
                Il cliente riceve la fattura via email.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
